import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import { getGradeDisplay } from '@data/utils/get-grade-range';
import BackButtonIcon from '@data/assets/backButton.svg';
import { Popover } from '@scholastic/mockingjay';
import InfoIcon from '@data/assets/info-icon.svg';
import { isSupportedProduct } from '@data/main';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';

interface HeaderCardProps {
  children?: ReactNode;
  productId?: string;
  /** Overrides the gradient accent bar's classes (colors and height). */
  accentClassName?: string;
}

const DEFAULT_ACCENT_CLASSES = 'h-3 from-purple-700 to-red-600';

export function HeaderCard({
  children,
  productId,
  accentClassName = DEFAULT_ACCENT_CLASSES,
}: HeaderCardProps) {
  const { data: applications, isLoading } = useApplications();

  const application = productId
    ? applications?.find((app) => app.applicationCode === productId)
    : undefined;

  const showBackButton =
    (applications?.filter((app) => isSupportedProduct(app.applicationCode))
      .length ?? 0) > 1;

  const gradeDisplay = application ? getGradeDisplay(application) : undefined;

  return (
    <div
      className={`relative mb-6 flex flex-row rounded-lg bg-white p-6 pt-9 shadow-md ${
        application ? 'items-center' : 'text-3xl font-semibold antialiased'
      }`}
    >
      <div
        className={`absolute left-0 top-0 w-full rounded-t-lg bg-gradient-to-r ${accentClassName}`}
      />
      {isLoading && (
        <div
          role="status"
          aria-label="Loading"
          className="flex w-full flex-col gap-2"
        >
          <LoadingBar className="h-9 w-full tablet:w-64" />
          <LoadingBar className="w-20" />
          <LoadingBar className="w-full max-w-[650px]" />
        </div>
      )}
      {!isLoading && application && (
        <>
          {showBackButton && (
            <div className="mr-3 flex-col items-start justify-start self-stretch">
              <Link
                to="/data/product-landing"
                aria-label="Back to product landing"
              >
                <BackButtonIcon aria-hidden className="h-11 w-11" />
              </Link>
            </div>
          )}

          <div className="flex-1 flex-col justify-start">
            <h1 className="flex items-center justify-between text-2xl font-bold antialiased tablet:text-3xl">
              {application.applicationLabel}
              {application.applicationDescription && (
                <Popover
                  trigger={
                    <button
                      aria-label={application.applicationDescription}
                      className="ml-2 h-[16px] w-[16px] tablet:hidden"
                      type="button"
                    >
                      <InfoIcon aria-hidden="true" />
                    </button>
                  }
                >
                  {application.applicationDescription}
                </Popover>
              )}
            </h1>

            {gradeDisplay && (
              <p className="mt-[2px] text-sm font-semibold text-[#333]">
                Grade{' '}
                <span className="text-[16px] font-normal">{gradeDisplay}</span>
              </p>
            )}

            {application.applicationDescription && (
              <p className="mt-2 hidden max-w-[650px] text-base text-neutral-900 tablet:flex">
                {application.applicationDescription}
              </p>
            )}
          </div>

          <div className="hidden items-center self-stretch tablet:flex">
            <img
              src={application.applicationIconSrc}
              alt={`${application.applicationLabel} logo`}
              className="ml-6 h-10 object-contain"
            />
          </div>
        </>
      )}
      {!isLoading && children}
    </div>
  );
}
